import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Send, Plus, X } from "lucide-react";

const ASSESSMENT_TYPES = [
  "Start of Care",
  "Resumption of Care",
  "Recertification",
  "Follow-up",
  "Discharge",
  "Transfer",
];

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function SubmitChart() {
  const [form, setForm] = useState({
    patient_name: "",
    assessment_date: "",
    assessment_type: "Start of Care",
    m1021_primary: "",
    notes: "",
    client_id: "",
  });
  const [clients, setClients] = useState([]);
  const [m1023, setM1023] = useState([{ slot: "A", code: "" }]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const updateSlot = (i, code) => {
    setM1023(m1023.map((row, idx) => (idx === i ? { ...row, code } : row)));
  };

  const addSlot = () => {
    if (m1023.length >= 26) return;
    const next = ALPHA[m1023.length];
    setM1023([...m1023, { slot: next, code: "" }]);
  };

  const removeSlot = (i) => {
    // Remove and re-letter remaining slots so they stay A,B,C...
    const filtered = m1023.filter((_, idx) => idx !== i);
    const relettered = filtered.map((row, idx) => ({ ...row, slot: ALPHA[idx] }));
    setM1023(relettered.length ? relettered : [{ slot: "A", code: "" }]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const selectedClient = clients.find((c) => c.id === form.client_id);
      const payload = {
        ...form,
        client: selectedClient?.name || "",
        m1021_primary: form.m1021_primary.trim().toUpperCase(),
        m1023_codes: m1023
          .map((r) => ({ slot: r.slot, code: r.code.trim().toUpperCase() }))
          .filter((r) => r.code),
      };
      await api.post("/charts", payload);
      toast.success("Chart submitted");
      setForm({ patient_name: "", assessment_date: "", assessment_type: "Start of Care", m1021_primary: "", notes: "", client_id: "" });
      setM1023([{ slot: "A", code: "" }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="submit-chart-page">
      <PageHeader title="Submit Chart" subtitle="Capture OASIS assessment and diagnosis codes." />

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-6" data-testid="submit-chart-form">
        {/* Patient & assessment header */}
        <div className="grid sm:grid-cols-4 gap-4">
          <div className="sm:col-span-1">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Patient Name</label>
            <input data-testid="patient-name-input" required value={form.patient_name}
                   onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                   placeholder="e.g. Eleanor Hayes"
                   className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Client</label>
            <select data-testid="client-select" value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
              <option value="">— Select —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Assessment Date</label>
            <input data-testid="assessment-date-input" type="date" required value={form.assessment_date}
                   onChange={(e) => setForm({ ...form, assessment_date: e.target.value })}
                   className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Assessment Type</label>
            <select data-testid="assessment-type-select" value={form.assessment_type}
                    onChange={(e) => setForm({ ...form, assessment_type: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
              {ASSESSMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* M1021 */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            <span className="inline-block bg-sky-50 text-sky-800 border border-sky-200 rounded px-2 py-0.5 text-xs font-mono mr-2">M1021</span>
            Primary Diagnosis
          </label>
          <input data-testid="m1021-input" required value={form.m1021_primary}
                 onChange={(e) => setForm({ ...form, m1021_primary: e.target.value.toUpperCase() })}
                 placeholder="e.g. E11.9"
                 className="w-full sm:w-64 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-sky-500 outline-none"/>
        </div>

        {/* M1023 A-Z */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-slate-700">
              <span className="inline-block bg-slate-100 text-slate-700 border border-slate-200 rounded px-2 py-0.5 text-xs font-mono mr-2">M1023</span>
              Other Diagnoses (A–Z)
            </label>
            <button type="button" onClick={addSlot} disabled={m1023.length >= 26}
                    data-testid="add-m1023-btn"
                    className="inline-flex items-center gap-1 text-sm text-sky-700 hover:text-sky-900 disabled:opacity-40 disabled:hover:text-sky-700">
              <Plus size={14}/> Add code
            </button>
          </div>
          <div className="space-y-2">
            {m1023.map((row, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`m1023-row-${row.slot}`}>
                <div className="w-10 h-9 grid place-items-center bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono font-medium text-slate-700">
                  {row.slot}
                </div>
                <input value={row.code}
                       onChange={(e) => updateSlot(i, e.target.value.toUpperCase())}
                       data-testid={`m1023-code-${row.slot}`}
                       placeholder={i === 0 ? "Required (M1023 A)" : "Optional"}
                       required={i === 0}
                       className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-sky-500 outline-none"/>
                {m1023.length > 1 && (
                  <button type="button" onClick={() => removeSlot(i)}
                          data-testid={`remove-m1023-${row.slot}`}
                          className="text-slate-400 hover:text-red-600 px-2">
                    <X size={16}/>
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">Slots are auto-lettered. Up to 26 codes (A–Z).</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes</label>
          <textarea data-testid="notes-input" rows={4} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Clinical context for QA review…"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
        </div>

        <div className="flex justify-end">
          <button data-testid="submit-chart-btn" disabled={submitting}
                  className="inline-flex items-center gap-2 bg-sky-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-sky-600 disabled:opacity-60">
            <Send size={16} /> {submitting ? "Submitting…" : "Submit chart"}
          </button>
        </div>
      </form>
    </div>
  );
}
